import api from './api';

export interface IOfferDashboardOffer {
  // The country of the department: SE offers are in SEK, the others in €
  country?: null | string;
  created: string;
  customerName: null | string;
  departmentId: null | number;
  departmentName: null | string;
  // An opportunity: the sum of its estimated rows
  estimate: null | number;
  expense: null | number;
  id: number;
  // NDA protected and the user is outside of the NDA: the name and sums are hidden
  ndaRestricted: boolean;
  revisionNumber: number;
  sentAt: null | string;
  status: string;
  targetName: string;
  type: string;
  value: null | number;
}

export interface IOfferDashboard {
  // The start of the period
  from: string;
  // The end of the period, not included
  to: null | string;
  // The sums of the positions of each offer by work type
  groups: { expense: number; group: string; offerId: number; value: number }[];
  offers: IOfferDashboardOffer[];
}

// An offer of the sales report: the latest revision of a preliminary offer, offer or
// material offer being calculated, sent or ordered, and the day it is reported by
export interface IOfferSalesOffer {
  // The code of an offer of SalesPro
  code?: null | string;
  // The country of the department: SE offers are in SEK, the others in €
  country?: null | string;
  created: string;
  customerName: null | string;
  departmentId: null | number;
  departmentName: null | string;
  expense: null | number;
  id: number;
  ndaRestricted: boolean;
  // The signer of the offer (tarjoaja)
  offeredBy: null | string;
  orderedAt: null | string;
  // Created when being calculated, sent when sent and ordered when ordered
  reportedAt: string;
  revisionNumber: number;
  sentAt: null | string;
  // Where the offer is from: ProBid, or imported from SalesPro (its expense may be unknown)
  source?: 'probid' | 'salespro';
  status: string;
  targetName: string;
  type: string;
  value: null | number;
  // The sums by work type (product group, empty without one) in their order in the offer,
  // empty when NDA protected
  workTypes: IOfferSalesWorkType[];
}

export interface IOfferSalesWorkType {
  expense: number;
  group: string;
  value: number;
}

// An offer imported from SalesPro, the earlier offer program, with the department of its owner
export interface ISalesProOffer {
  calculationStartedAt: null | string;
  code: string;
  country?: null | string;
  currency: null | string;
  customerName: null | string;
  departmentId: null | number;
  departmentName: null | string;
  expense: null | number;
  id: number;
  importedAt: string;
  importedBy: null | string;
  margin: null | number;
  // A share, e.g. 0.25
  marginRatio: null | number;
  name: string;
  owner: null | string;
  project: null | string;
  requestedSubmissionDate: null | string;
  // The deadline of the offer, the day it was sent
  sentAt: null | string;
  // Empty while open, Voitettu (won) or Hävitty (lost)
  status: null | string;
  total: null | number;
}

// What an import of SalesPro did
export interface ISalesProImport {
  count: number;
  importedAt: string;
  // The owners not found from the users of ProBid; their offers are without a department
  ownersNotFound: string[];
}

// A column of a report exported to Excel: its title and an Excel number format for numbers
export interface IReportExportColumn {
  numberFormat?: string;
  title: string;
}

// An open preliminary offer or offer of the report by work type (its latest revision)
export interface IOpenWorkTypeOffer {
  // The country of the department: SE offers are in SEK, the others in €
  country?: null | string;
  departmentId: null | number;
  departmentName: null | string;
  id: number;
  revisionNumber: number;
  status: string;
  targetName: string;
  type: string;
}

// A position of an open offer: its work type (product group) and its sums
export interface IOpenWorkTypePosition {
  code: null | string;
  expense: number;
  // Empty without a work type
  group: string;
  id: number;
  name: null | string;
  offerId: number;
  quantity: number;
  unit: null | string;
  value: number;
}

// A material row of a position of an open offer: its sums for the whole position and the
// supplier of its product in the registry
export interface IOpenWorkTypeMaterial {
  expense: number;
  id: number;
  name: null | string;
  offerId: number;
  positionId: number;
  quantity: number;
  // null without a supplier in the registry
  supplier: null | string;
  supplierId: null | number;
  unit: null | string;
  // Typed by hand, without a product of the registry
  unknown: boolean;
  value: number;
}

export interface IOpenWorkTypes {
  // The material rows of the positions, for the report by supplier
  materials?: IOpenWorkTypeMaterial[];
  offers: IOpenWorkTypeOffer[];
  positions: IOpenWorkTypePosition[];
  // The open offers protected by an NDA the user is outside of, left out
  restrictedCount: number;
}

// An offer on the offer timeline: its contract and the start and end of the whole site
export interface IOfferTimelineRow {
  departmentId: null | number;
  departmentName: null | string;
  // The installation hours, null when hidden by an NDA
  hours: null | number;
  id: number;
  ndaRestricted: boolean;
  siteEnds: null | string;
  siteStarts: null | string;
  status: string;
  targetName: string;
  type: string;
  workEnds: null | string;
  workStarts: null | string;
  // The work types of the positions with their hours, in their order in the offer
  workTypes: IOfferTimelineWorkType[];
}

// A work type (product group, empty without one) of an offer and its installation hours
export interface IOfferTimelineWorkType {
  group: string;
  hours: number;
}

// One visit to an offer or a position: who, when, for how long and whether saved
export interface IOfferLogEntry {
  // A position was open for calculating, not only for looking
  calculated: boolean;
  department: null | string;
  // A status change: the status before it and the new one
  fromStatus: null | string;
  toStatus: null | string;
  // Unique in the list
  key: string;
  // visit = the offer or a position was open, status = the status was changed
  kind: 'visit' | 'status';
  offerId: number;
  offerType: string;
  openedAt: string;
  // The code and name of the position, null for the offer page
  position: null | string;
  savedAt: null | string;
  targetName: null | string;
  // When the page was closed or last reported itself
  until: string;
  user: string;
  visitId: number;
}

export interface IOfferLogPage {
  entries: IOfferLogEntry[];
  total: number;
}

export default {
  // The offers created during the period for the offer dashboard, the end not included
  getOfferDashboard: (from: Date, to: Date): Promise<IOfferDashboard> =>
    api.get('/api/v1/reports/offer-dashboard', {
      from: from.toISOString(),
      to: to.toISOString(),
    }),
  // The offers being calculated, sent and ordered during the period, the end not included
  getOfferSales: (
    from: Date,
    to: Date,
    // The open offers imported from SalesPro too
    includeSalesPro = false
  ): Promise<IOfferSalesOffer[]> =>
    api.get('/api/v1/reports/offer-sales', {
      from: from.toISOString(),
      includeSalesPro,
      to: to.toISOString(),
    }),
  // The offers imported from SalesPro
  getSalesPro: (): Promise<ISalesProOffer[]> =>
    api.get('/api/v1/reports/salespro'),
  // An Excel export of SalesPro, which replaces the offers imported before
  importSalesPro: (file: File): Promise<ISalesProImport> => {
    const data = new FormData();
    data.append('file', file);
    return api.postFile('/api/v1/reports/salespro', data);
  },
  // A table of a report as an Excel file, its rows as shown
  exportReport: (request: {
    columns: IReportExportColumn[];
    fileName: string;
    rows: (null | number | string)[][];
    sheetName: string;
  }): Promise<{ data: Blob; headers: Record<string, string> }> =>
    api.postForFile('/api/v1/reports/export', request),
  // The positions of the open preliminary offers and offers, for the report by work type
  // of the offers made during the last months, or all without months
  getOpenWorkTypes: (months: null | number): Promise<IOpenWorkTypes> =>
    api.get(
      '/api/v1/reports/open-work-types',
      months === null ? undefined : { months }
    ),
  // The offers with dates of their contract or site, for the offer timeline
  getOfferTimeline: (): Promise<IOfferTimelineRow[]> =>
    api.get('/api/v1/reports/offer-timeline'),
  // The log of all offers, for the users with the right to read the log
  getOfferLog: (filters: {
    limit: number;
    offset: number;
    search?: string;
    since?: string;
  }): Promise<IOfferLogPage> => api.get('/api/v1/reports/offer-log', filters),
  // The history of one offer, for its maker and the log readers
  getOfferHistory: (
    id: number,
    isAdditionalOffer: boolean
  ): Promise<IOfferLogPage> =>
    api.get(
      isAdditionalOffer
        ? `/api/v1/additionaloffers/${id}/history`
        : `/api/v1/offers/${id}/history`
    ),
};
